import { FC, useState, useEffect } from "react";
import styled from "styled-components";
import { Flex, Table } from "src/components/ui";
import { Feedback, PaginationInfo } from "src/types/feedback";
import { formatDate } from "src/utils";
import { FeedbackActionMenu } from "./FeedbackActionMenu";
import { FeedbackDetailsModal } from "./FeedbackDetailsModal";
import { ConfirmationModal } from "src/components/ui/ConfirmationModal";
import { usePagination } from "src/hooks";
import { PaginationProps } from "../ui/Pagination";

const FeedbacksTableContainer = styled.div`
	background-color: ${(props) => props.theme.colors.white};
	border-radius: 16px;
	padding: 20px 0;

	.feedback-status {
		padding: 6px 12px;
		border-radius: 16px;
		font-weight: 500;
		display: inline-block;

		&.very-satisfied {
			background: #e6f4ea;
			color: #1e8e3e;
		}

		&.satisfied {
			background: #e8f0fe;
			color: #1a73e8;
		}

		&.neutral {
			background: #f1f3f4;
			color: #5f6368;
		}

		&.unsatisfied {
			background: #fef7e6;
			color: #f29d38;
		}

		&.very-unsatisfied {
			background: #fce8e8;
			color: #d93025;
		}
	}

	.action-menu {
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;

		&:hover {
			background: ${(props) => props.theme.colors.gray_02};
		}
	}
`;

interface Props {
	feedbacks: Feedback[];
	pgn: number;
	setpgn: React.Dispatch<React.SetStateAction<number>>;
	paginationData: PaginationProps;
	onDelete: (feedback: Feedback) => Promise<void>;
	onPin: (feedback: Feedback) => Promise<void>;
	isLoading?: boolean;
	activeTab: string;
}

export const FeedbacksTable: FC<Props> = ({
	feedbacks,
	pgn,
	setpgn,
	paginationData,
	onDelete,
	onPin,
	isLoading = false,
	activeTab,
}) => {
	const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(
		null
	);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(
		null
	);

	const { page, limit, Pagination } = usePagination(
		{
			page: pgn,
			limit: 10,
			total: paginationData.total, // Use total from paginationData instead of feedbacks.length
			setPageNo: (newPage: number) => {
				setpgn(newPage);
			},
		},
		paginationData
	);

	const paginatedRows = feedbacks; // Use the full feedbacks array since server already paginates

	const handleDelete = (feedback: Feedback) => {
		setFeedbackToDelete(feedback);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		if (feedbackToDelete) {
			try {
				setIsDeleting(true);
				await onDelete(feedbackToDelete);
			} catch (error) {
				console.error("Error deleting feedback:", error);
			} finally {
				setIsDeleting(false);
				setIsDeleteModalOpen(false);
				setFeedbackToDelete(null);
			}
		}
	};

	const FeedbacksTableHeaders = [
		{
			title: "User",
			render: (row: Feedback) => (
				<Flex gap='10px' align='center'>
					<img
						src={row.display_picture}
						alt='User'
						className='rounded-full object-cover w-10 h-10'
					/>
					<div>
						<div
							style={{ fontWeight: 500 }}
						>{`${row.first_name} ${row.last_name}`}</div>
						<div style={{ color: "#666", fontSize: "0.9em" }}>{row.email}</div>
					</div>
				</Flex>
			),
		},
		{
			title: "Phone Number",
			render: (row: Feedback) => row.phone,
		},

		{
			title: "Rating",
			render: (row: Feedback) => row.emojiRating || "N/A",
		},
		{
			title: "Remarks",
			render: (row: Feedback) => (
				<div
					style={{
						maxWidth: "300px",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{row.feedback}
				</div>
			),
		},
		{
			title: "Date",
			render: (row: Feedback) => formatDate(row.submitted_at),
		},
		{
			title: "App Version",
			render: (row: Feedback) => row.appVersion || "N/A",
		},
		{
			title: "",
			render: (row: Feedback) => (
				<FeedbackActionMenu
					feedback={row}
					onView={() => setSelectedFeedback(row)}
					onDelete={() => handleDelete(row)}
					onPin={() => onPin(row)}
					isPinned={activeTab.includes("pinned_")}
				/>
			),
		},
	];

	return (
		<>
			<FeedbacksTableContainer>
				{feedbacks.length > 0 ? (
					<>
						<Table
							rows={paginatedRows}
							headers={FeedbacksTableHeaders}
							showHead={true}
						/>
						{feedbacks.length > 0 && <Pagination />}
					</>
				) : (
					<h5 className='font-bold text-center py-8 italic text-2xl'>
						No Feedbacks
					</h5>
				)}
			</FeedbacksTableContainer>

			{selectedFeedback && (
				<FeedbackDetailsModal
					feedback={selectedFeedback}
					onClose={() => setSelectedFeedback(null)}
				/>
			)}

			<ConfirmationModal
				open={isDeleteModalOpen}
				handleClose={() => {
					setIsDeleteModalOpen(false);
					setFeedbackToDelete(null);
				}}
				approve={confirmDelete}
				message={
					feedbackToDelete
						? `Are you sure you want to delete the feedback from ${feedbackToDelete.first_name} ${feedbackToDelete.last_name}? This action cannot be undone.`
						: "Are you sure you want to delete this feedback?"
				}
				loading={isDeleting}
			/>
		</>
	);
};
